/**
 * Layer preferences persistence service.
 *
 * Pure data layer — no dependency on map instances, services, or components.
 * Accepts state as plain objects and returns plain objects.
 */

import { LAYER_TIER_MAP } from '../lib/layer-tier-registry.js'
import type { LayerState, LayerStateList } from '../lib/layer-state-store.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TerrainOverlayState {
  'slope-angle': boolean
  'sun-exposure': boolean
  'sun-exposure-mode': string
  'land-cover': boolean
  'land-cover-classes': string // JSON-encoded number[]
  contours: boolean
  'contour-color': string
  'snow-depth': boolean
  'blm-sma': boolean
  padus: boolean
  'padus-style-mode': string
  'wildlife-inat': boolean
  'wildlife-inat-taxa': string
  'wildlife-inat-time-filter': boolean
  'wildlife-critical-habitat': boolean
  'wildlife-nps-alerts': boolean
  'wildlife-nps-categories': string
  'cellular-carriers': string
  'cellular-show-tower-cov': boolean
  'cellular-show-towers': boolean
  'cellular-technologies': string
  /** Dynamic keys for NWM services and climbing filters */
  [key: string]: boolean | string | number
}

export interface PanelLayerEntry {
  id: string
  visible: boolean
  children?: PanelLayerEntry[]
}

export interface LayerPreferences {
  baseLayer: string
  enabledOverlays: string[]
  terrainOverlays: TerrainOverlayState
  overlayOpacities: Record<string, number>
  subdailyOverlays: string[]
  compositeOverlays: string[]
  cloudMaskOverlays: string[]
  favoriteLayers: string[]
  panelLayers?: PanelLayerEntry[]
  tierOrderOverrides?: Record<string, number>
}

// ---------------------------------------------------------------------------
// Storage keys (kept consistent with the originals in layer-switcher.ts)
// ---------------------------------------------------------------------------

export const STORAGE_KEYS = {
  BASE_LAYER: 'cairn_selected_base_layer',
  OVERLAYS: 'cairn_enabled_overlays',
  TERRAIN_OVERLAYS: 'cairn_terrain_overlays',
  OVERLAY_OPACITIES: 'cairn_overlay_opacities',
  SUBDAILY: 'cairn_subdaily_overlays',
  COMPOSITE: 'cairn_composite_overlays',
  CLOUD_MASK: 'cairn_cloud_mask_overlays',
  FAVORITE_LAYERS: 'cairn_favorite_layers',
  PANEL_LAYERS: 'cairn_panel_layers',
  TIER_ORDER: 'cairn_tier_order_overrides',
  /** Unified per-layer state (single source of truth). */
  LAYER_STATE: 'cairn_layer_state',
} as const

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_TERRAIN_STATE: TerrainOverlayState = {
  'slope-angle': false,
  'sun-exposure': false,
  'sun-exposure-mode': 'exposure',
  'land-cover': false,
  'land-cover-classes': '[]',
  contours: false,
  'contour-color': '#8B4513',
  'snow-depth': false,
  'blm-sma': false,
  padus: false,
  'padus-style-mode': 'gap-status',
  'wildlife-inat': false,
  'wildlife-inat-taxa': '[40151]',
  'wildlife-inat-time-filter': false,
  'wildlife-critical-habitat': false,
  'wildlife-nps-alerts': false,
  'wildlife-nps-categories': '["Danger","Caution","Park Closure","Information"]',
  graticule: false,
  'utm-grid': false,
  'mgrs-grid': false,
  declination: false,
  'range-rings': false,
  plss: false,
  backcountry: false,
  cotrex: false,
  'cotrex-closures': false,
  satellite: false,
  'satellite-los': false,
  'fire-wildfires': false,
  'fire-wildfires-types': '["WF","RX","CX"]',
  'fire-firms': false,
  'fire-firms-sensors': '["VIIRS NOAA-21","Landsat"]',
  'fire-firms-confidence': '["nominal","high"]',
  'fire-restrictions': false,
  'fire-restrictions-sources': '["nps","usfws"]',
  'climbing-crags': false,
  'cellular-carriers': '[]',
  'cellular-show-tower-cov': false,
  'cellular-show-towers': false,
  'cellular-technologies': '[]',
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Serialize the full layer preferences state to localStorage.
 *
 * Each logical group is stored under its own key so that partial reads (e.g.
 * restoring overlays after async WMTS load) remain cheap.
 */
export function saveLayerPreferences(prefs: LayerPreferences): void {
  localStorage.setItem(STORAGE_KEYS.BASE_LAYER, prefs.baseLayer)
  localStorage.setItem(STORAGE_KEYS.OVERLAYS, JSON.stringify(prefs.enabledOverlays))
  localStorage.setItem(STORAGE_KEYS.TERRAIN_OVERLAYS, JSON.stringify(prefs.terrainOverlays))
  localStorage.setItem(STORAGE_KEYS.OVERLAY_OPACITIES, JSON.stringify(prefs.overlayOpacities))
  localStorage.setItem(STORAGE_KEYS.SUBDAILY, JSON.stringify(prefs.subdailyOverlays))
  localStorage.setItem(STORAGE_KEYS.COMPOSITE, JSON.stringify(prefs.compositeOverlays))
  localStorage.setItem(STORAGE_KEYS.CLOUD_MASK, JSON.stringify(prefs.cloudMaskOverlays))
  localStorage.setItem(STORAGE_KEYS.FAVORITE_LAYERS, JSON.stringify(prefs.favoriteLayers))
  if (prefs.panelLayers) {
    localStorage.setItem(STORAGE_KEYS.PANEL_LAYERS, JSON.stringify(prefs.panelLayers))
  }
  if (prefs.tierOrderOverrides) {
    localStorage.setItem(STORAGE_KEYS.TIER_ORDER, JSON.stringify(prefs.tierOrderOverrides))
  }
}

/**
 * Load layer preferences from localStorage.
 *
 * Returns `null` when **no** preference data exists at all (first launch).
 * Any corrupt or partially-missing keys are replaced with safe defaults so the
 * caller always receives a complete `LayerPreferences` object (or null).
 */
export function loadLayerPreferences(): LayerPreferences | null {
  try {
    const baseLayer = localStorage.getItem(STORAGE_KEYS.BASE_LAYER)

    // If there is no base-layer key we treat this as "never saved"
    if (baseLayer === null) {
      return null
    }

    const favoriteLayers = parseJsonArray(STORAGE_KEYS.FAVORITE_LAYERS)
    const rawTierOverrides = parseJsonRecord(STORAGE_KEYS.TIER_ORDER)
    const tierOrderOverrides = migrateFavoritesToTierOverrides(favoriteLayers, rawTierOverrides)

    const panelLayers = parsePanelLayers()

    const result: LayerPreferences = {
      baseLayer,
      enabledOverlays: parseJsonArray(STORAGE_KEYS.OVERLAYS),
      terrainOverlays: parseTerrainOverlays(),
      overlayOpacities: parseJsonRecord(STORAGE_KEYS.OVERLAY_OPACITIES),
      subdailyOverlays: parseJsonArray(STORAGE_KEYS.SUBDAILY),
      compositeOverlays: parseJsonArray(STORAGE_KEYS.COMPOSITE),
      cloudMaskOverlays: parseJsonArray(STORAGE_KEYS.CLOUD_MASK),
      favoriteLayers,
    }
    if (panelLayers.length > 0) result.panelLayers = panelLayers
    if (Object.keys(tierOrderOverrides).length > 0) result.tierOrderOverrides = tierOrderOverrides
    return result
  } catch {
    // Complete storage failure — treat as first launch
    return null
  }
}

/**
 * Remove all layer preference keys from localStorage.
 */
export function clearLayerPreferences(): void {
  for (const key of Object.values(STORAGE_KEYS)) {
    localStorage.removeItem(key)
  }
}

// ---------------------------------------------------------------------------
// Unified layer-state persistence + legacy migration
// ---------------------------------------------------------------------------

/**
 * Remove the legacy scattered layer-state keys that the unified store now owns
 * (panelLayers, enabledOverlays, render-mode sets, favorites). Keeps the unified
 * key (LAYER_STATE), the base layer, and tier-order overrides.
 *
 * Also keeps TERRAIN_OVERLAYS and OVERLAY_OPACITIES: these are NOT superseded by
 * the unified store — they hold per-layer sub-settings (class filters, render
 * modes, colors, filter sets) and per-layer opacity that the unified on/off model
 * does not represent. They are still actively written by saveLayerPreferences and
 * read by restoreOverlayPreferences (which applies them via setters only, never
 * re-toggling on/off), so purging them silently wiped those settings on every load.
 */
export function clearLegacyLayerKeys(): void {
  const keep = new Set<string>([
    STORAGE_KEYS.LAYER_STATE,
    STORAGE_KEYS.BASE_LAYER,
    STORAGE_KEYS.TIER_ORDER,
    STORAGE_KEYS.TERRAIN_OVERLAYS,
    STORAGE_KEYS.OVERLAY_OPACITIES,
  ])
  for (const key of Object.values(STORAGE_KEYS)) {
    if (!keep.has(key)) localStorage.removeItem(key)
  }
}

/** Persist the unified per-layer state list to localStorage. */
export function saveLayerStateList(list: LayerStateList): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LAYER_STATE, JSON.stringify(list))
  } catch {
    /* storage full / unavailable — non-fatal */
  }
}

/**
 * Load the unified per-layer state. Precedence:
 *   1. The unified key, if present.
 *   2. Otherwise migrate from the legacy LayerPreferences (favorites/panel/overlays/
 *      terrain/opacities/render-modes), so existing users keep their setup.
 *   3. Otherwise null (first launch).
 * Always pruned to ids that exist in LAYER_TIER_MAP.
 */
export function loadLayerStateList(): LayerStateList | null {
  // 1. Unified key.
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LAYER_STATE)
    if (raw !== null) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed.filter(isRegistryRecord)
    }
  } catch {
    /* fall through to legacy migration */
  }

  // 2. Legacy migration.
  const legacy = loadLayerPreferences()
  if (!legacy) return null
  return migrateLegacyToLayerState(legacy)
}

function isRegistryRecord(rec: unknown): rec is LayerState {
  return (
    typeof rec === 'object' &&
    rec !== null &&
    typeof (rec as Record<string, unknown>).id === 'string' &&
    LAYER_TIER_MAP.has((rec as Record<string, unknown>).id as string)
  )
}

/**
 * Build the unified per-layer state from the legacy scattered stores. Maps:
 *   favoriteLayers      -> favorite:true
 *   panelLayers[].visible -> enabled:true, visible:<v>  (incl. children)
 *   enabledOverlays     -> enabled+visible:true
 *   terrainOverlays[id] booleans -> enabled+visible:true
 *   overlayOpacities    -> opacity
 *   subdaily/composite/cloudMask + per-layer model keys -> options
 * Drops ids not present in LAYER_TIER_MAP.
 */
export function migrateLegacyToLayerState(legacy: LayerPreferences): LayerStateList {
  const byId = new Map<string, LayerState>()
  const ensure = (id: string): LayerState | null => {
    if (!LAYER_TIER_MAP.has(id)) return null
    let s = byId.get(id)
    if (!s) {
      s = { id, enabled: false, visible: false, favorite: false }
      byId.set(id, s)
    }
    return s
  }

  // panelLayers → enabled + visible (recurse children).
  const applyPanel = (entries: PanelLayerEntry[] | undefined): void => {
    for (const e of entries ?? []) {
      const s = ensure(e.id)
      if (s) {
        s.enabled = true
        s.visible = e.visible
      }
      if (e.children) applyPanel(e.children)
    }
  }
  applyPanel(legacy.panelLayers)

  // enabledOverlays (WMS/GIBS) → enabled + visible.
  for (const id of legacy.enabledOverlays ?? []) {
    const s = ensure(id)
    if (s) {
      s.enabled = true
      s.visible = true
    }
  }

  // terrainOverlays boolean keys that are ids → enabled + visible.
  for (const [key, val] of Object.entries(legacy.terrainOverlays ?? {})) {
    if (val === true && LAYER_TIER_MAP.has(key)) {
      const s = ensure(key)
      if (s) {
        s.enabled = true
        s.visible = true
      }
    }
  }

  // favorites.
  for (const id of legacy.favoriteLayers ?? []) {
    const s = ensure(id)
    if (s) s.favorite = true
  }

  // opacities.
  for (const [id, op] of Object.entries(legacy.overlayOpacities ?? {})) {
    const s = ensure(id)
    if (s && typeof op === 'number' && Number.isFinite(op)) s.opacity = op
  }

  // render-mode options.
  const flag = (ids: string[] | undefined, key: string): void => {
    for (const id of ids ?? []) {
      const s = ensure(id)
      if (s) s.options = { ...(s.options ?? {}), [key]: true }
    }
  }
  flag(legacy.subdailyOverlays, 'subdaily')
  flag(legacy.compositeOverlays, 'composite')
  flag(legacy.cloudMaskOverlays, 'cloudMask')

  // per-layer model pins live in terrainOverlays as `${id}-model` string values.
  for (const [key, val] of Object.entries(legacy.terrainOverlays ?? {})) {
    if (typeof val === 'string' && key.endsWith('-model')) {
      const id = key.slice(0, -'-model'.length)
      const s = ensure(id)
      if (s && val !== 'auto') s.options = { ...(s.options ?? {}), model: val }
    }
  }

  return [...byId.values()]
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function parseJsonArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function parseJsonRecord(key: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return {}
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      const result: Record<string, number> = {}
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'number' && !Number.isNaN(v)) result[k] = v
      }
      return result
    }
    return {}
  } catch {
    return {}
  }
}

function migrateFavoritesToTierOverrides(
  favoriteLayers: string[],
  tierOrderOverrides: Record<string, number>
): Record<string, number> {
  if (Object.keys(tierOrderOverrides).length > 0 || favoriteLayers.length === 0) {
    return tierOrderOverrides
  }

  const tierBuckets = new Map<number, string[]>()
  for (const id of favoriteLayers) {
    const entry = LAYER_TIER_MAP.get(id)
    if (!entry) continue
    const bucket = tierBuckets.get(entry.tier) ?? []
    bucket.push(id)
    tierBuckets.set(entry.tier, bucket)
  }

  const migrated: Record<string, number> = {}
  for (const ids of tierBuckets.values()) {
    for (let i = 0; i < ids.length; i++) {
      migrated[ids[i]] = (i + 1) * 10
    }
  }
  return migrated
}

function parsePanelLayers(): PanelLayerEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PANEL_LAYERS)
    if (raw === null) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (item: unknown): item is PanelLayerEntry =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as Record<string, unknown>).id === 'string' &&
        typeof (item as Record<string, unknown>).visible === 'boolean'
    )
  } catch {
    return []
  }
}

function parseTerrainOverlays(): TerrainOverlayState {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.TERRAIN_OVERLAYS)
    if (raw === null) return { ...DEFAULT_TERRAIN_STATE }
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      // Merge with defaults so missing keys get safe values
      return { ...DEFAULT_TERRAIN_STATE, ...parsed }
    }
    return { ...DEFAULT_TERRAIN_STATE }
  } catch {
    return { ...DEFAULT_TERRAIN_STATE }
  }
}
